/**
 * Onboarding API service file
 *
 * @package    src/services
 * @author     PeterTuring <peter.a@turing.com>
 * @copyright  2018 Turing Company
 * @license    Turing License
 * @version    1.0
 */
import axios from 'axios'
import { ApiConstant } from '../constants'



/**
 * Fetch Role Skill mapping data from developer service API
 */
export function fetchRoleSkillsMappingData() {
    const token = localStorage.getItem('token');

    if (token) {
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        product: 'dev-eng-frontend',
      }
      return axios.get(`${ApiConstant.REACT_APP_DEVELOPER_SERVICE_API}/v1/skills-by-role`, { headers })
    } else {
      return Promise.reject(new Error('Expected token'));
    }
}

/**
 * Get all skill list
 * @param {boolean} get_canonical_skills 
 * @returns response object
 */
export function getBaseAllSkillList(get_canonical_skills) {
  const token = localStorage.getItem('token');

  if (token) {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
    const body = {
      action: 'GET_BASE_ALL_SKILL_LIST',
      get_canonical_skills
    };
    return axios.post(`${ApiConstant.BASE_URL}common/`, body, { headers })
  } else {
    return Promise.reject(new Error('Expected token'));
  }
}

/**
 * Adds a new user skill data.
 * @param {Object} skill - The skill object to add.
 * @returns {Promise} A Promise that resolves with the response data if successful, otherwise rejects with an error.
 */
export function addUserSkillData (skill) {
  const token = localStorage.getItem('token');

  if (token) {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
    const body = {
      skill
    };
    return axios.post(`${ApiConstant.BASE_URL}${ApiConstant.ADD_USER_SKILL_DATA}`, body, { headers })
  } else {
    return Promise.reject(new Error('Expected token'));
  }
}

/**
 * Updates a user skill data.
 * @param {Object} skill - The updated skill object.
 * @returns {Promise} A Promise that resolves with the response data if successful, otherwise rejects with an error.
 */
export function updateUserSkillData(skill) {
  const token = localStorage.getItem('token');

  if (token) {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
    const body = {
      skill
    };
    return axios.put(`${ApiConstant.BASE_URL}${ApiConstant.UPDATE_USER_SKILL_DATA}`, body, { headers })
  } else {
    return Promise.reject(new Error('Expected token'));
  }
}

/**
 * Deletes a user skill data.
 * @param {string} skill_id - The ID of the skill to delete.
 * @returns {Promise} A Promise that resolves with the response data if successful, otherwise rejects with an error.
 */
export function deleteUserSkillData(skill_id) {
  const token = localStorage.getItem('token');

  if (token) {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
    const body = {
      id: skill_id
    };
    return axios.post(`${ApiConstant.BASE_URL}${ApiConstant.DELETE_USER_SKILL_DATA}`, body, { headers })
  } else {
    return Promise.reject(new Error('Expected token'));
  }
}

/**
 * Gets recommended jobs for low supply.
 * @param {string} skill_id - The ID of the skill to delete.
 * @returns {Promise} A Promise that resolves with the response data if successful, otherwise rejects with an error.
 */
export function getRecommendedJobForLowSupply(body) {
  const token = localStorage.getItem('token');

  if (token) {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
    const body = {
      action: "GET_RECOMMENDED_JOBS_FOR_LOW_SUPPLY",
      ...body,
    };
    return axios.post(`${ApiConstant.BASE_URL}${ApiConstant.JOB_APPLY}`, body, { headers })
  } else {
    return Promise.reject(new Error('Expected token'));
  }
}

/**
 * Gets all languages.
 * @returns {Promise} A Promise that resolves with the response data if successful, otherwise rejects with an error.
 */
export function getAllLanguages() {
  const token = localStorage.getItem('token');

  if (token) {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
   
    return axios.get(`${ApiConstant.BASE_URL}languages`, { headers })
  } else {
    return Promise.reject(new Error('Expected token'));
  }
}

/**
 * Gets all proficiency types and levels.
 * @returns {Promise} A Promise that resolves with the response data if successful, otherwise rejects with an error.
 */
export function getAllProficiencyTypesAndLevels() {
  const token = localStorage.getItem('token');

  if (token) {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
   
    return axios.get(`${ApiConstant.BASE_URL}${ApiConstant.FETCH_LANGUAGE_PROFICIENCY_TYPE_AND_LEVELS}`, { headers })
  } else {
    return Promise.reject(new Error('Expected token'));
  }
}

/**
 * Saves the languages selected by the user.
 * @returns {Promise} A Promise that resolves with the response data if successful, otherwise rejects with an error.
 */
export function saveAllLanguages(body) {
  const token = localStorage.getItem('token');

  if (token) {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }

    return axios.post(`${ApiConstant.BASE_URL}developer/languages`, body, { headers })
  } else {
    return Promise.reject(new Error('Expected token'));
  }
}

/**
 * Removes the languages unselected by the user.
 * @returns {Promise} A Promise that resolves with the response data if successful, otherwise rejects with an error.
 */
export function removeAllLanguages(body) {
  const token = localStorage.getItem('token');

  if (token) {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }

    return axios.post(`${ApiConstant.BASE_URL}developer/languages/remove`, body, { headers })
  } else {
    return Promise.reject(new Error('Expected token'));
  }
}

/**
 * Gets all developer languages.
 * @returns {Promise} A Promise that resolves with the response data if successful, otherwise rejects with an error.
 */
export function getAllDeveloperLanguages() {
  const token = localStorage.getItem('token');

  if (token) {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
   
    return axios.get(`${ApiConstant.BASE_URL}developer/languages`, { headers })
  } else {
    return Promise.reject(new Error('Expected token'));
  }
}

export function getDeveloperLanguageProficiencies() {
  const token = localStorage.getItem('token');

  if (token) {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
   
    return axios.get(`${ApiConstant.BASE_URL}${ApiConstant.FETCH_DEVELOPER_LANGUAGE_PROFICIENCY}`, { headers })
  } else {
    return Promise.reject(new Error('Expected token'));
  }
}

export function saveDeveloperLanguageProficiencies(body) {
  const token = localStorage.getItem('token');

  if (token) {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }

    return axios.post(`${ApiConstant.BASE_URL}developer/languages/proficiency`, body, { headers })
  } else {
    return Promise.reject(new Error('Expected token'));
  }
}